/* eslint-disable react/no-array-index-key */
import React, {
  useRef, useCallback, useContext, useEffect, useState
} from 'react';
import { string, number } from 'prop-types';
import { BrowseSearchRedirector, DynamicComponent } from '@thd-nucleus/app-render';
import classNames from 'classnames';
import {
  client,
  extend,
  QueryProvider,
  QueryContext,
  hoist,
  useLazyDataModel,
  params,
  bool as boolType,
  shape as shapeType,
  string as stringType,
  alias
} from '@thd-nucleus/data-sources';
import { useHistory, useLocation } from '@thd-olt-component-react/router';
import { Row, Col } from '@thd-olt-component-react/grid';
import {
  ProductResults,
  ResultsApplied,
  ResultsFilterAndSort,
  ResultsHeader,
  ResultsPagination,
  ResultsWrapped,
  ResultsSortBy,
  ResultsDimensions,
  useProductResults,
  productDefaultResultsDataModel,
  updateQueryParams,
  parseUrl,
  ProductResultsContext
} from '@thd-olt-component-react/product-results';
import { ProductPodDefault, ProductPodPlaceholder, ProductPodUtils } from '@thd-olt-component-react/product-pod';
import { RectShape, TextBlock } from '@thd-olt-component-react/loading-placeholder';
import {
  ProductPodGroup,
  CustomProductPodDetails
} from '@thd-olt-component-react/product-pod-group';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { CategoryCardsDesktop } from '@thd-olt-component-react/category-cards';
import { LayoutManager, LayoutGroup } from '@thd-olt-component-react/layout-manager';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { SearchFeedback } from '@thd-olt-component-react/search-feedback';
import { RelatedSearch } from '@thd-olt-component-react/related-search';
import { EtchWidgets } from '@thd-olt-component-react/etch-widgets';
import { VisualCategoryNavigation } from '@thd-olt-component-react/visual-category-navigation';
import { BackToTop } from '@thd-olt-component-react/back-to-top';
import {
  RecommendationsProvider,
  RecommendationsPlaceholder
} from '@thd-olt-component-react/recommendations';
import { Metadata, BrowseSearchMetadata } from '@thd-olt-component-react/metadata';
import { CompareTray, useComparableCategory } from '@thd-olt-component-react/compare';
import { AisleBay, AisleBayPodsDataProvider, AisleBayPodsContext } from '@thd-olt-component-react/aisle-bay';
import { DynamicBroadIntentAisle } from '@thd-olt-component-react/dynamic-broad-intent-aisle';
import { NoResultsFound } from '@thd-olt-component-react/no-results-found';
import { RentalIntentDesktop } from '@thd-olt-component-react/rental-intent';
import { TrackOrder } from '@thd-olt-component-react/track-order';
import { Hydrator, declareContexts } from '@thd-olt-component-react/hydrator';
import {
  SeoGeneratedContentLoad, SeoGeneratedContent, SeoGeneratedContentModel,
  SeoLinksLoad, SeoLinks, SeoLinksModel,
} from './dynamic-imports';
import '../../styles/browse-search.scss';
import { KEY_PRODUCT_FEATURE_POD_CATEGORIES, HYDRATION_STANDARD_SCROLL_BUFFER } from '../../constants';

const HoistedProductPodGroup = hoist(ProductPodGroup, ProductPodDefault, CategoryCardsDesktop, CustomProductPodDetails);

export const BrowseAndSearch = (props) => {
  declareContexts([ProductResultsContext, QueryContext, ExperienceContext, AisleBayPodsContext]);
  const history = useHistory();
  const {
    channel,
    isDebugMode,
    clientStore,
    cookie,
    configService,
    isServer,
    deliveryZip: serverDeliveryZip,
  } = useContext(ExperienceContext);
  const { search: querystring, pathname } = useLocation();
  const path = `${pathname}${querystring}`;
  const { pageType, prefetchPip, productPodHoverDelay } = props;
  const isSearch = pageType === 'search';
  const isBrowse = pageType === 'browse';
  const prefetchedSkuSet = useRef(new Set());
  const disableSSR = typeof window !== 'undefined' && window.DISABLE_SSR;
  const varnish = !disableSSR && !isSearch;
  const store = useStore({ varnish, online: false });
  const pageLoadVariables = useRef(null);
  const isNoResultsFoundRef = useRef(false);
  const { storeId, storeZip } = store;

  const SERVER_SIDE_PRODUCT_POD_COUNT = 12;
  const CLIENT_SIDE_PRODUCT_POD_COUNT = 24;
  const searchNavPropsRef = useRef({
    pageSize: disableSSR ? CLIENT_SIDE_PRODUCT_POD_COUNT : SERVER_SIDE_PRODUCT_POD_COUNT,
    askForSponsored: false,
    deliveryZip: serverDeliveryZip,
  });

  if (clientStore) {
    let deliveryZipCode;
    if (typeof window !== 'undefined' && window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails) {
      deliveryZipCode = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails();
    }
    searchNavPropsRef.current = {
      pageSize: CLIENT_SIDE_PRODUCT_POD_COUNT,
      askForSponsored: true,
      deliveryZip: deliveryZipCode?.zipcode,
    };
  }

  const HEADER_OFFSET = 59;

  const { isClientResolved, setDefaultVariables } = useContext(QueryContext);

  const renderPlaceholder = (browseScheme = '', searchScheme = '', defer = true) => {
    const schemeName = isBrowse ? browseScheme : searchScheme;

    if (!schemeName) return null;

    const isBanner = ['cat_sponsored__banner', 'browse_sponsored__banner']
      .indexOf(browseScheme) > -1
      || searchScheme === 'search_sponsored__banner';

    let lgName = `recs:${schemeName}`;
    if (schemeName.indexOf('sponsored') > -1) {
      if (isBanner) {
        lgName = 'sponsored-banner';
      } else {
        lgName = 'sponsored-carousel';
      }
    }

    const gridClass = classNames({ 'grid--card grid-space-top': !isBanner });
    return (
      <LayoutGroup name={`${lgName}`}>
        <Row>
          <Col nopadding>
            <div className={gridClass}>
              <RecommendationsPlaceholder scheme={schemeName} defer={defer} />
            </div>
          </Col>
        </Row>
      </LayoutGroup>
    );
  };

  const onStoreChange = ({ output }) => {
    const { storeId: updatedStoreId } = output || {};
    if (!updatedStoreId) return;

    if (window && window.THD_LOCALIZER_AUTO_INIT && window.THD_LOCALIZER_AUTO_INIT.Localizer) {
      window.THD_LOCALIZER_AUTO_INIT.Localizer
        .localize({ storeId: updatedStoreId })
        .then(() => {
          window.location.reload();
        });
    }
  };

  const makeStickyHeader = () => {
    const el = document.querySelector('.Header3');
    if (el) {
      const headerClass = ['Header3-sticky-desktop'];
      el.classList.add(...headerClass);
    }
  };

  const positionRef = useRef({ lastScrollPosition: 0 });

  // eslint-disable-next-line consistent-return
  const handleScroll = useCallback(() => {
  }, []);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.on('product-results.change-store', onStoreChange);
    makeStickyHeader();

  }, []);

  const onDimensionsChange = useCallback(({ refinement }) => {
    window.LIFE_CYCLE_EVENT_BUS.trigger('filters-refinements.click', refinement);

    history.push(refinement.url);
  });

  const onMultiStoreSelectionChange = useCallback(({ multiStoreIds }) => {
    const updatedPath = updateQueryParams(path, [
      {
        paramName: 'storeSelection',
        paramValue: multiStoreIds
      }
    ]);
    history.push(updatedPath);
  });

  const resultsDimensionsRef = useRef(null);
  const onPageChange = useCallback(({ url }) => {
    history.push(url);

    const { offsetTop } = resultsDimensionsRef?.current?.parentElement?.parentElement;
    window.scrollTo(0, offsetTop || 0);
  });

  const onSortChange = useCallback(({
    sortby, sortorder, name, currentPage, page, updatedPath = null
  }) => {
    const newPath = updateQueryParams(updatedPath || path, [
      {
        paramName: 'Nao',
        paramValue: null
      },
      {
        paramName: 'sortorder',
        paramValue: sortorder
      },
      {
        paramName: 'sortby',
        paramValue: sortby
      }
    ]);
    window.LIFE_CYCLE_EVENT_BUS.trigger('sort-refinements.click', {
      name,
      currentPage,
      page
    });
    history.push(newPath);
  });

  const cleanPath = path.replace(/^\/render[^/]*/, '');

  const isEtchVisNavOnPLP = (data) => {
    try {
      const stringifiedPartialTemplates = data?.searchModel?.partialTemplates;
      const etchVisualNavExists = (stringifiedPartialTemplates?.[0] || '').indexOf('visualNavigation') > -1;
      const hasPLPBanner = data?.searchModel?.metadata?.hasPLPBanner;
      const isPLPPage = data?.searchModel?.metadata?.contentType === 'plppage';
      return hasPLPBanner && etchVisualNavExists && isPLPPage;
    } catch (err) {
      console.log('Error parsing etch templates');
      return false;
    }
  };

  const {
    keyword,
    lowerbound,
    Nao = 0,
    sortby,
    nValue = '',
    sortorder,
    upperbound,
    storeSelection,
    catStyle,
    skipSpellCheck
  } = parseUrl({ path: cleanPath });

  const showKeyProductFeaturePods = KEY_PRODUCT_FEATURE_POD_CATEGORIES.find((value) => nValue.includes(value));

  const loadedStoreId = useRef(storeId);
  const timeoutRef = useRef(null);
  const [productPageChunkLoaded, setProductPageChunkLoaded] = useState(false);

  const additionalSearchParams = {};
  const hasSponsoredSorts = !sortby || ['bestmatch', 'topsellers'].indexOf(sortby) > -1;
  if (searchNavPropsRef.current.askForSponsored && hasSponsoredSorts && cookie?.adobeCookie?.MCMID) {
    additionalSearchParams.sponsored = true;
    additionalSearchParams.mcvisId = cookie.adobeCookie.MCMID;
  }

  let storefilter = 'ALL';
  if (storeSelection) {
    additionalSearchParams.multiStoreIds = [`${storeSelection}`];
    storefilter = 'IN_STORE';
  }

  if (searchNavPropsRef.current.deliveryZip) {
    additionalSearchParams.deliveryZip = searchNavPropsRef.current.deliveryZip;
  }

  const showProducts = /showproducts/i.test(catStyle);
  if (showProducts) {
    additionalSearchParams.plp = true;
  }

  if (skipSpellCheck) {
    additionalSearchParams.skipSpellCheck = skipSpellCheck;
  }

  const opts = {
    additionalSearchParams,
    keyword,
    lowerbound,
    nValue,
    pageSize: searchNavPropsRef.current.pageSize,
    storeId,
    storefilter,
    start: Nao,
    sortby,
    sortorder,
    upperbound
  };

  const response = useProductResults(opts);

  const { data, error, loading, variables } = response;

  const cleanedPath = (pathname || '').replace(/render-\w+\//, '');

  if (!pageLoadVariables.current) {
    pageLoadVariables.current = variables;
    setDefaultVariables(variables);
  }
  if (data && !loading) {
    loadedStoreId.current = variables.storeId;
  }

  const handleProductClick = useCallback((evt, product) => {
    let canonicalUrl = product?.identifiers?.canonicalUrl;

    if (canonicalUrl) {
      evt.preventDefault();
      canonicalUrl = ProductPodUtils.addSponsoredAnalyticsParams({
        productUrl: canonicalUrl, info: product.info
      });
      history.push(canonicalUrl);
    }
  }, []);

  const isCategory = (data?.searchModel?.metadata?.contentType
    && data.searchModel.metadata.contentType === 'categorypage');

  const { products = [], metadata } = data?.searchModel || {};

  const stringifyItemIds = (hookData) => {
    return hookData?.searchModel?.products?.map(({ itemId: sku }) => sku)
      .join('-');
  };

  const stringifyAppliedRefinements = (hookData) => {
    const appliedDimensions = hookData?.searchModel?.appliedDimensions || [];
    return appliedDimensions.map(({ refinements = [] }) => {
      return refinements.map((refinement) => refinement.refinementKey).join('-');
    }).join('-');
  };

  const [selectedForCompare, setSelectedForCompare] = useState([]);

  const onSelectedForCompare = (id, selected = false) => {
    if (selected) {
      setSelectedForCompare([...selectedForCompare, id]);
    } else {
      setSelectedForCompare(selectedForCompare.filter((item) => item !== id));
    }
  };

  const onCompare = () => {
    const queryparams = selectedForCompare
      .map((item, idx) => `item${idx + 1}=${item}`)
      .join('&');

    history.push({
      pathname: '/compare',
      search: `?${queryparams}`
    });
  };

  const hasInStoreFilter = (resultsData) => {
    const { searchModel } = resultsData || {};
    const { appliedDimensions } = searchModel || {};
    const getItFast = (appliedDimensions || [])
      .filter((dimension) => (dimension.label || '').toUpperCase() === 'GET IT FAST');
    if (!getItFast.length) {
      return false;
    }
    const storePickUp = getItFast
      .find((dimension) => dimension.refinements
        .find((refinement) => refinement.label === 'Pick Up Today'));
    return !!storePickUp;
  };

  useEffect(() => {
    const itemIds = window.sessionStorage.getItem('compare');
    window.sessionStorage.removeItem('compare');
    if (itemIds) {
      setSelectedForCompare(JSON.parse(itemIds));
    }
  }, []);

  const resolved = isClientResolved({ queryName: 'searchModel' });
  useEffect(() => {
    if (data && !loading) {
      if (isBrowse && resolved) {
        LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('browse.new-products', data?.searchModel);
      } else if (isSearch && resolved) {
        LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('search.new-products', data?.searchModel);
      }
    }
  }, [stringifyItemIds(data), resolved, stringifyAppliedRefinements(data)]);

  const resultsColProps = {
    xs: '8',
    sm: '9',
    md: '10',
    lg: '10'
  };

  const prefetchPipRef = useRef({
    prefetchPip: alias('product').params({ itemId: stringType() }).shape({
      itemId: stringType()
    }),
    prefetchPipClient: alias('product').params({ itemId: stringType() }).shape({
      itemId: stringType()
    })
  });

  const dataModelCombo = extend({}, prefetchPipRef.current.prefetchPip, prefetchPipRef.current.prefetchPipClient);

  const [productRequestClient, productResponseClient] = useLazyDataModel('prefetchPipClient', {
    fetchPolicy: 'cache-first',
    dataModel: { prefetchPipClient: dataModelCombo },
    skip: true,
    variables: { storeId, dataSource: 'catalog' }
  });

  const fetchProduct = (itemId) => {
    if (prefetchPip === 'both' || prefetchPip === 'clientOnly') {
      productRequestClient({
        variables: {
          itemId,
          dataSource: 'catalog',
          storeId,
          zipCode: storeZip
        }
      });
    }
    prefetchedSkuSet.current.add(itemId);
  };

  const productPageImport = async () => {
    if (timeoutRef?.current) {
      clearTimeout(timeoutRef.current);
    }
    return import(
      /* webpackChunkName: "product" */
      '../../pages/product/ProductContainer'
    );
  };

  const loadProductPageChunk = async () => {
    if (!productPageChunkLoaded) {
      const productPage = await productPageImport();
      prefetchPipRef.current = productPage.ProductDataModel;
      setProductPageChunkLoaded(true);
    }
  };

  useEffect(() => {
    if (prefetchPip) {
      timeoutRef.current = setTimeout(() => {
        loadProductPageChunk();
      }, 5000);
    }
  }, []);

  const onHoverProductPod = (itemId) => {
    if (prefetchPip) {
      loadProductPageChunk();
    }

    if (productPageChunkLoaded && !prefetchedSkuSet.current.has(itemId)) {
      fetchProduct(itemId);
    }
  };

  const getResxFromData = () => {

    const shouldFetchBanner = (() => {
      const hasBrandCrumb = data?.searchModel?.taxonomy?.breadCrumbs?.some((crumb) => crumb.dimensionName === 'Brand');
      const isFeaturedProductNode = pathname.indexOf('/b/Featured-Products') !== -1;

      const hasBrandSearch = data?.searchModel?.metadata?.analytics?.semanticTokens
        && data.searchModel.metadata.analytics.semanticTokens.indexOf('br:{') !== -1;

      return !(hasBrandCrumb || hasBrandSearch || isFeaturedProductNode);
    })();
    const categoryID = data?.searchModel?.metadata?.categoryID;

    const itemIds = data?.searchModel?.products?.map((product) => {
      return product.identifiers?.itemId;
    });

    const levels = data?.searchModel?.taxonomy?.breadCrumbs?.reduce((acc, cur) => {
      if (!cur.dimensionName) return acc;
      return `${acc}, ${cur.label}`;
    }, '');

    return {
      appId: 'homedepot01',
      carouselOptions: {
        fullScrollSkip: 2
      },
      criteo: {
        version: 'model2'
      },
      dataContext: {
        searchModel: data?.searchModel
      },
      enableEventSchema: true,
      fetchBanner: shouldFetchBanner,
      itemid: categoryID,
      keyword,
      levels,
      maxProducts: 16,
      organicskus: itemIds ? itemIds.toString() : '',
      products: itemIds ? itemIds.toString() : '',
      recsAndSearchDynamicAPIEnabled: true,
      storeId,
    };
  };

  const skipFn = ({ skip, queryName, attributes }) => {
    if (queryName !== 'product') return skip;
    const isResolved = isClientResolved({ queryName: 'searchModel' });
    if (attributes.product.fulfillment) {
      if (isResolved) return skip;
      return true;
    }
    return skip;
  };

  const isInStoreFilterON = hasInStoreFilter(data);
  const pageNumber = (Nao / searchNavPropsRef.current.pageSize) + 1;
  const comparableCategory = useComparableCategory({ categoryID: data?.searchModel?.metadata?.categoryID });
  const isEtchVisNavInPLP = isEtchVisNavOnPLP(data);
  const isDynamicBrandPage = data?.searchModel?.aisles?.length > 0;

  const storeSkuIds = resolved ? products?.map((product) => product.identifiers?.storeSkuNumber)
    .filter((sku) => !!sku) : [];
  const isPLPSearchPage = (isSearch || isBrowse);

  isNoResultsFoundRef.current = isSearch
    && data?.searchModel?.searchReport?.totalProducts === 0
    && !!keyword;

  const maxNumOfPodsToHydrate = 12;

  const productsReduce = (productPods) => {
    let groupOfPods = 0;

    let reduced = productPods?.reduce((hydrator, product) => {
      if (!hydrator[groupOfPods]) {
        hydrator.push([]);
      }
      if (hydrator[groupOfPods].length < maxNumOfPodsToHydrate) {
        hydrator[groupOfPods].push(product);
        if (hydrator[groupOfPods].length === maxNumOfPodsToHydrate) {
          groupOfPods += 1;
        }
      }
      return hydrator;
    }, []);

    if (productPods.length === SERVER_SIDE_PRODUCT_POD_COUNT && (!isClientResolved({ queryName: 'searchModel' }))) {
      const placeholderHydrators = (CLIENT_SIDE_PRODUCT_POD_COUNT - SERVER_SIDE_PRODUCT_POD_COUNT)
        / maxNumOfPodsToHydrate;
      for (let i = 0; i < placeholderHydrators; i += 1) {
        reduced.push(Array(maxNumOfPodsToHydrate).fill());
      }
    }

    return reduced;
  };

  const originalProducts = useRef(productsReduce(products));
  const [isHydrated, setIsHydrated] = useState(false);
  const hydrators = isHydrated || !originalProducts.current?.length
    ? productsReduce(products)
    : originalProducts.current;

  setDefaultVariables({
    ...variables
  });

  if (isNoResultsFoundRef.current) {
    return (
      <>
        <TrackOrder keyword={keyword} />
        <NoResultsFound keyword={keyword} recommendationsCtx={getResxFromData()} />
      </>
    );
  }

  return (
    <>
      <LayoutManager name="browse-search-redirector-wrapper">
        <LayoutGroup name="browse-search-redirector">
          <BrowseSearchRedirector variables={response?.variables} />
        </LayoutGroup>
      </LayoutManager>
      <RecommendationsProvider resx={getResxFromData()}>
        <Row className="isBound browse-search">
          <LayoutManager name="browse-and-search-master" input={{ isDebugMode }} useContextForProps>
            <LayoutGroup name="browse-and-search-metadata">
              <Metadata>
                <BrowseSearchMetadata data={data} loading={loading} error={error} pathWithQueryParams={path} />
              </Metadata>
            </LayoutGroup>
            <LayoutGroup>
              <TrackOrder keyword={keyword} />
            </LayoutGroup>
            <LayoutGroup tag={Row} name="breadcrumbs" className="u--paddingTop u--paddingBottom">
              <Col flatten>
                <Breadcrumbs data={data} loading={loading} error={error} />
              </Col>
            </LayoutGroup>
            <LayoutGroup tag={Row} name="rental-intent">
              <Col flatten>
                <RentalIntentDesktop data={data} loading={loading} error={error} keyword={keyword} />
              </Col>
            </LayoutGroup>
            <LayoutGroup tag={Row} name="product-results-group">
              <Col flatten>
                <ProductResults data={data} loading={loading} error={error}>
                  <LayoutManager
                    name="product-results"
                    input={{ isDebugMode, isDynamicBrandPage, channel }}
                    useContextForProps
                  >
                    {renderPlaceholder(
                      isCategory ? 'cat_sponsored__banner' : 'browse_sponsored__banner',
                      'search_sponsored__banner',
                      false
                    )}
                    <LayoutGroup tag={Row} name="results-header">
                      <Col flatten>
                        <ResultsHeader
                          showShopAllLink={isDynamicBrandPage}
                          resultsRef={resultsDimensionsRef}
                        />
                      </Col>
                    </LayoutGroup>
                    <LayoutGroup tag={Row} name="etch-widgets">
                      <EtchWidgets
                        data={data}
                        loading={loading}
                        error={error}
                        keyword={keyword}
                        navParam={nValue}
                        storeId={loadedStoreId.current}
                        exclude={['plpSeo']}
                      />
                    </LayoutGroup>
                    {!isEtchVisNavInPLP && (
                      <LayoutGroup tag={Row} name="visual-nav">
                        <Col className="visual-nav-container">
                          <VisualCategoryNavigation
                            data={data}
                            loading={loading}
                            disableSwipeNavigation
                            slider
                            showArrows={false}
                          />
                        </Col>
                      </LayoutGroup>
                    )}
                    {!isCategory && (
                      <LayoutGroup tag={Row} name="results-applied">
                        <Col sm="9">
                          <ResultsApplied onAppliedChange={onDimensionsChange} />
                        </Col>
                        <Col xs="3">
                          <ResultsSortBy onSortChange={onSortChange} />
                        </Col>
                      </LayoutGroup>
                    )}
                    {!isCategory && (
                      <LayoutGroup
                        tag={Row}
                        name="results-dimensions"
                        className="browse-search__left-outer-padding"
                      >
                        <Col xs="4" sm="3" md="2" lg="2">
                          <Hydrator
                            id="browse-search-dimensions"
                          >
                            <ResultsDimensions
                              onDimensionsChange={onDimensionsChange}
                              enableMultiStore
                              onMultiStoreSelectionChange={onMultiStoreSelectionChange}
                              multiStoreSelection={storeSelection}
                              shiftCategoryDimension={isDynamicBrandPage}
                            />
                          </Hydrator>
                        </Col>
                        <Col
                          className="browse-search__pod-col-no-padding"
                          /* eslint-disable-next-line react/jsx-props-no-spreading */
                          {...resultsColProps}
                        >
                          <LayoutManager
                            useContextForProps
                            name="product-results-inner"
                            input={{ isDebugMode, isDynamicBrandPage, channel }}
                          >
                            {isDynamicBrandPage && (
                              <LayoutGroup name="dynamic-aisles">
                                <DynamicBroadIntentAisle
                                  searchResponse={response}
                                  storeId={loadedStoreId.current}
                                />
                              </LayoutGroup>
                            )}
                            <LayoutGroup tag={Row} name="results-applied">
                              <Col flatten>
                                <Col sm="9">
                                  <ResultsApplied onAppliedChange={onDimensionsChange} />
                                </Col>
                                <Col xs="3">
                                  <ResultsSortBy onSortChange={onSortChange} />
                                </Col>
                              </Col>
                            </LayoutGroup>
                            <LayoutGroup name="wrapped-results">
                              <ResultsWrapped>
                                <div className="grid" ref={resultsDimensionsRef}>
                                  {!products?.length && loading && isSearch && (
                                    <ProductPodPlaceholder podsCount={SERVER_SIDE_PRODUCT_POD_COUNT} />
                                  )}
                                  {!!hydrators?.length && (
                                    <AisleBayPodsDataProvider
                                      storeSkuIds={storeSkuIds}
                                      hasInStoreFilter={hasInStoreFilter(data)}
                                      isSearchPage={isPLPSearchPage}
                                    >
                                      <QueryProvider
                                        dataSource="searchNav"
                                        cacheKey="search-product-pods"
                                        defaultVariables={{ storeId }}
                                        skip={skipFn}
                                      >
                                        {hydrators?.map((group, hydratorIndex) => {
                                          if (!group?.length) return null;
                                          return (
                                            <Hydrator
                                              afterHydrate={() => { setIsHydrated(true); }}
                                              id={`browse-search-pods-${hydratorIndex + 1}`}
                                              className="grid"
                                              scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                                              patch={!hydratorIndex}
                                              key={`hydrator-${hydratorIndex}`}
                                            >
                                              <HoistedProductPodGroup
                                                DetailsPod={CustomProductPodDetails}
                                                keyword={keyword}
                                              >
                                                {group?.map((product, itemIndex, productArray) => {
                                                  const { itemId } = product?.identifiers || {};
                                                  if (!itemId) {
                                                    return (
                                                      <Col
                                                        className="placeholder-product-pod"
                                                        key={`placeholder-${itemIndex}`}
                                                        lg="3"
                                                        md="3"
                                                        sm="3"
                                                        xs="4"
                                                      >
                                                        <RectShape
                                                          style={{
                                                            width: '100%',
                                                            height: '250px',
                                                            marginBottom: '10px'
                                                          }}
                                                          color="#E0E0E0"
                                                        />
                                                        <TextBlock rows={7} color="#E0E0E0" />
                                                      </Col>
                                                    );
                                                  }
                                                  const showContentCard = (
                                                    (hydratorIndex * maxNumOfPodsToHydrate) + itemIndex === 12
                                                  );
                                                  const podCount = productArray?.length;
                                                  const fallback = channel === 'desktop';
                                                  return (
                                                    <LayoutGroup
                                                      index={itemIndex}
                                                      key={`${itemId}-${itemIndex}`}
                                                      id={`plp-product-pod__${itemIndex}`}
                                                      name={`Product Pod: ${itemIndex}`}
                                                    >
                                                      {showContentCard
                                                      && (
                                                        <CategoryCardsDesktop
                                                          data={data}
                                                          loading={loading}
                                                          error={error}
                                                          storeId={loadedStoreId.current}
                                                        />
                                                      )}

                                                      <Col
                                                        lg={showKeyProductFeaturePods ? 4 : 3}
                                                        md={showKeyProductFeaturePods ? 4 : 3}
                                                        sm="4"
                                                        xs="6"
                                                        fallback={fallback}
                                                        className="browse-search__pod"
                                                      >
                                                        <ProductPodDefault
                                                          itemId={itemId}
                                                          storeId={loadedStoreId?.current}
                                                          onClick={handleProductClick}
                                                          position={itemIndex}
                                                          mobilePodType="singleColumn"
                                                          installationServices
                                                          showModelNumber
                                                          showStoreInfo
                                                          podCount={podCount}
                                                          pageNumber={pageNumber}
                                                          onHover={onHoverProductPod}
                                                          pageType={pageType}
                                                          hoverDelay={productPodHoverDelay}
                                                          // preFetchProductData
                                                          comparable
                                                          onCompare={onCompare}
                                                          selectedForCompare={selectedForCompare}
                                                          onSelectedForCompare={onSelectedForCompare}
                                                          showKPF={showKeyProductFeaturePods}
                                                          hasInStoreFilter={isInStoreFilterON}
                                                        />
                                                      </Col>
                                                    </LayoutGroup>
                                                  );
                                                })}
                                              </HoistedProductPodGroup>
                                            </Hydrator>
                                          );
                                        })}
                                      </QueryProvider>
                                    </AisleBayPodsDataProvider>
                                  )}
                                </div>
                              </ResultsWrapped>
                            </LayoutGroup>
                            <LayoutGroup>
                              {(products?.length < SERVER_SIDE_PRODUCT_POD_COUNT) && (
                                <CategoryCardsDesktop data={data} loading={loading} error={error} />)}
                            </LayoutGroup>
                            <LayoutGroup tag={Row} className="u--paddingTop u--paddingBottom" name="compare">
                              <Col>
                                <CompareTray
                                  itemIds={selectedForCompare}
                                  onRemove={onSelectedForCompare}
                                  onCompare={onCompare}
                                />
                              </Col>
                            </LayoutGroup>
                            {isSearch && (
                              <DynamicComponent pageType={pageType}>
                                <Hydrator
                                  id="search-seo-section"
                                  className="grid"
                                  scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                                >
                                  <LayoutGroup
                                    tag={Row}
                                    name="related-search"
                                    className="u--paddingTop u--paddingBottom"
                                  >
                                    <Col nopadding>
                                      <RelatedSearch
                                        navParam={nValue}
                                        searchData={data}
                                        searchError={error}
                                        searchLoading={loading}
                                        storeId={loadedStoreId.current}
                                      />
                                    </Col>
                                  </LayoutGroup>
                                </Hydrator>
                              </DynamicComponent>
                            )}
                            <DynamicComponent pageType={pageType}>
                              <LayoutGroup tag={Row} className="u--paddingTop u--paddingBottom" name="pagination">
                                <Col>
                                  <ResultsPagination
                                    pageSize={searchNavPropsRef.current.pageSize}
                                    onPageChange={onPageChange}
                                    path={path}
                                  />
                                </Col>
                              </LayoutGroup>
                              <Hydrator
                                id="feedback"
                                className="grid"
                                scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                              >
                                <LayoutGroup
                                  tag={Row}
                                  className="u--paddingTop u--paddingBottom"
                                  name="search-feedback"
                                >
                                  <Col>
                                    <SearchFeedback />
                                  </Col>
                                </LayoutGroup>
                              </Hydrator>
                            </DynamicComponent>
                          </LayoutManager>
                        </Col>
                      </LayoutGroup>
                    )}
                  </LayoutManager>
                </ProductResults>
              </Col>
            </LayoutGroup>
            {renderPlaceholder(
              '',
              'plpsearch_multi'
            )}
            {renderPlaceholder(
              isCategory ? 'cat_sponsored' : 'browse_sponsored',
              'search_sponsored',
              false
            )}
            {renderPlaceholder(
              'PLP_Browse',
              'searchViewed'
            )}
            {renderPlaceholder(
              isCategory ? 'rv_categorypages_rr' : 'rv_nav_plp_rr',
              'rv_search_plp_rr'
            )}
            {!isSearch && (
              <DynamicComponent pageType={pageType}>
                <Hydrator
                  id="browse-seo-section"
                  className="grid"
                  waitFor={[SeoGeneratedContentLoad, SeoLinksLoad]}
                  scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                >
                  <LayoutGroup tag={Row} name="etch-seo">
                    <Col>
                      <EtchWidgets data={data} loading={loading} error={error} include={['plpSeo']} />
                    </Col>
                  </LayoutGroup>
                  <LayoutGroup tag={Row} name="related-search" className="u--paddingTop u--paddingBottom">
                    <Col nopadding>
                      <RelatedSearch
                        navParam={nValue}
                        searchData={data}
                        searchError={error}
                        searchLoading={loading}
                        storeId={loadedStoreId.current}
                      />
                    </Col>
                  </LayoutGroup>
                  <LayoutGroup tag={Row} className="u--paddingTop u--paddingBottom" name="seo-qanda">
                    <Col>
                      <SeoGeneratedContent nvalue={nValue} />
                    </Col>
                  </LayoutGroup>
                  <LayoutGroup tag={Row} name="seo-mmg" className="u--paddingTop u--paddingBottom">
                    <Col nopadding>
                      <SeoLinks path={cleanedPath} />
                    </Col>
                  </LayoutGroup>
                </Hydrator>
              </DynamicComponent>
            )}
          </LayoutManager>
          <BackToTop />
        </Row>
      </RecommendationsProvider>
    </>
  );
};

BrowseAndSearch.displayName = 'BrowseAndSearchPage';

BrowseAndSearch.propTypes = {
  pageType: string.isRequired,
  prefetchPip: string,
  productPodHoverDelay: number
};

BrowseAndSearch.defaultProps = {
  prefetchPip: null,
  productPodHoverDelay: 500
};

BrowseAndSearch.dataModel = extend({
  searchModel: params().shape({
    metadata: shapeType({
      hasPLPBanner: boolType(),
      categoryID: stringType(),
      analytics: shapeType({
        semanticTokens: stringType(),
        dynamicLCA: stringType()
      })
    }),
    products: params().arrayOf(shapeType({
      identifiers: shapeType({
        storeSkuNumber: client(stringType())
      })
    }))
  })
},
ResultsDimensions,
ResultsFilterAndSort,
BrowseSearchRedirector,
BrowseSearchMetadata,
Breadcrumbs,
EtchWidgets,
VisualCategoryNavigation,
AisleBay,
AisleBayPodsDataProvider,
CategoryCardsDesktop,
CompareTray,
SeoGeneratedContentModel,
SeoLinksModel,
RelatedSearch,
RentalIntentDesktop,
RecommendationsPlaceholder,
productDefaultResultsDataModel,
TrackOrder,
DynamicBroadIntentAisle
);
